import { Observable } from 'rxjs';
import UserField = facebook.UserField;
import { BaseLoginProvider } from './base-login-provider';
import { SocialUser } from '@personal-area/global/social-authorization/models';

export class FacebookLoginProvider extends BaseLoginProvider {
  public static readonly PROVIDER_ID: string = 'FACEBOOK';

  private _requestOptions = {
    scope: 'email,public_profile',
    locale: 'en_US',
    fields: 'name,email,picture,first_name,last_name',
    version: 'v20.0',
  };

  constructor(
    private _clientId: string,
    initOptions: Record<string, unknown> = {},
  ) {
    super();

    this._requestOptions = {
      ...this._requestOptions,
      ...initOptions,
    };
  }

  public initialize(): Observable<void> {
    window['fbAsyncInit'] = () => {
      FB.init({
        appId: this._clientId,
        autoLogAppEvents: true,
        cookie: true,
        xfbml: true,
        version: this._requestOptions.version,
      });
    };

    return this._loadScript(
      FacebookLoginProvider.PROVIDER_ID,
      `//connect.facebook.net/${this._requestOptions.locale}/sdk.js`,
    );
  }

  public getLoginStatus(): Observable<SocialUser> {
    return new Observable<SocialUser>(observer => {
      FB.getLoginStatus((response) => {
        if (response.status === 'connected') {
          const authResponse = response.authResponse;
          FB.api(`/me?fields=${this._requestOptions.fields}`, (fbUser: {[key in UserField]: string }) => {
            const user: SocialUser = new SocialUser();

            user.id = fbUser.id;
            user.name = fbUser.name;
            user.email = fbUser.email;
            user.photoUrl =
              'https://graph.facebook.com/' +
              fbUser.id +
              '/picture?type=normal&access_token=' +
              authResponse.accessToken;
            user.firstName = fbUser.first_name;
            user.lastName = fbUser.last_name;
            user.authToken = authResponse.accessToken;

            user.response = fbUser;

            observer.next(user);
            observer.complete();
          });
        } else {
          observer.error(
            `No user is currently logged in with ${FacebookLoginProvider.PROVIDER_ID}`
          );
        }
      });
    });
  }

  public signIn(signInOptions: any = {}): Observable<SocialUser> {

    const options = {...this._requestOptions, ...signInOptions};
    return new Observable<SocialUser>(observer => {
      FB.login((response) => {
        if (response.authResponse) {
          const authResponse = response.authResponse;
          FB.api<{ [keyof in UserField]: string }>(`/me?fields=${options.fields}`, (fbUser) => {
            const user: SocialUser = new SocialUser();

            user.id = fbUser.id;
            user.name = fbUser.name;
            user.email = fbUser.email;
            user.photoUrl =
              'https://graph.facebook.com/' +
              fbUser.id +
              '/picture?type=normal';
            user.firstName = fbUser.first_name;
            user.lastName = fbUser.last_name;
            user.authToken = authResponse.accessToken;

            user.response = fbUser;

            observer.next(user);
            observer.complete();
          });
        } else {
          observer.error('User cancelled login or did not fully authorize.');
        }
      }, options);
    });
  }

  public signOut(): Observable<void> {
    return new Observable(observer => {
      FB.logout((response) => {
        observer.next();
        observer.complete();
      });
    });
  }
}
